import React from 'react';

/**
 * Mapeo de variantes de badges a clases visuales de BEM
 */
const BADGE_MODIFIERS = {
  upcoming: 'Upcoming',
  info: 'Info',
  success: 'Success',
  active: 'Active',
  warning: 'Upcoming',
};

/**
 * Mapeo de variantes de botones a clases visuales de BEM
 */
const BUTTON_MODIFIERS = {
  primary: 'Primary',
  excel: 'Excel',
  success: 'Excel',
  info: 'Info',
  secondary: 'Secondary',
  purple: 'Purple',
};

/**
 * Mapeo de iconos contextuales según la variante del badge
 */
const BADGE_ICONS = {
  upcoming: 'bi bi-clock-history',
  info: 'bi bi-stars',
  success: 'bi bi-check-circle-fill',
  active: 'bi bi-check-circle-fill',
  warning: 'bi bi-exclamation-triangle-fill',
};

/**
 * Componente reutilizable de tarjeta de reporte para dinámicas de Interaktia (HU-03).
 * Renderiza la cabecera con icono temático y badge de estado, descripción,
 * contenido contextual opcional (listas de características, contadores) y botón
 * interactivo con estados de carga (spinner) y desactivación.
 *
 * @param {Object} props
 * @param {string} props.title - Título principal de la dinámica (ej. "Mensajes de Apoyo")
 * @param {string} [props.icon] - Clase de Bootstrap Icons para el icono principal
 * @param {string} [props.iconVariant] - Modificador de color para el contenedor del icono ('blue' | 'green' | etc.)
 * @param {string} [props.badgeText] - Texto que se muestra en el badge superior
 * @param {string} [props.badgeVariant='upcoming'] - Estilo del badge ('upcoming' | 'info' | 'success' | 'active')
 * @param {string} [props.description] - Descripción o resumen funcional de la dinámica
 * @param {string} [props.buttonText='Descargar'] - Texto visible en el botón de acción
 * @param {string} [props.buttonIcon] - Clase de Bootstrap Icons para el botón
 * @param {string} [props.buttonVariant='excel'] - Variante visual del botón ('excel' | 'primary' | 'success')
 * @param {Function} [props.onDownload] - Callback a ejecutar al pulsar el botón
 * @param {boolean} [props.isLoading=false] - Indica si la acción está en progreso (muestra spinner)
 * @param {boolean} [props.isDisabled=false] - Deshabilita el botón
 * @param {React.ReactNode} [props.children] - Elementos adicionales dentro del cuerpo de la tarjeta
 */
export default function ReportCard({
  title,
  icon,
  iconVariant,
  badgeText,
  badgeVariant = 'upcoming',
  description,
  buttonText = 'Descargar',
  buttonIcon,
  buttonVariant = 'excel',
  onDownload,
  isLoading = false,
  isDisabled = false,
  children,
}) {
  const normalizedBadgeKey = (badgeVariant || '').toLowerCase();
  const badgeModifier = BADGE_MODIFIERS[normalizedBadgeKey] || 'Upcoming';
  const badgeIconClass = BADGE_ICONS[normalizedBadgeKey] || 'bi bi-info-circle-fill';

  const normalizedBtnKey = (buttonVariant || '').toLowerCase();
  const btnModifier = BUTTON_MODIFIERS[normalizedBtnKey] || 'Excel';

  const isButtonDisabled = Boolean(isDisabled || isLoading);

  // Modificador de color para la caja de icono principal
  const iconBoxModifier = iconVariant
    ? `AdmReports__Card__IconBox--${iconVariant.charAt(0).toUpperCase() + iconVariant.slice(1).toLowerCase()}`
    : '';

  // Clases del botón de acción
  const buttonClassNames = [
    'AdmReports__Card__Button',
    isButtonDisabled && !isLoading ? 'AdmReports__Card__Button--Disabled' : `AdmReports__Card__Button--${btnModifier}`,
    isLoading ? 'AdmReports__Card__Button--Loading' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (e) => {
    if (isButtonDisabled) {
      e.preventDefault();
      return;
    }
    if (typeof onDownload === 'function') {
      onDownload(e);
    }
  };

  return (
    <article className="AdmReports__Card" aria-label={`Tarjeta de reporte: ${title}`}>
      {/* Cabecera Superior: Icono y Badge */}
      <div className="AdmReports__Card__Top">
        <div className={`AdmReports__Card__IconBox ${iconBoxModifier}`.trim()}>
          {icon && <i className={icon} aria-hidden="true"></i>}
        </div>

        {badgeText && (
          <span className={`AdmReports__Card__Badge AdmReports__Card__Badge--${badgeModifier}`}>
            <i className={`${badgeIconClass} me-1`} aria-hidden="true"></i>
            {badgeText}
          </span>
        )}
      </div>

      {/* Contenido Central: Título, Descripción y Elementos Adicionales */}
      <div className="AdmReports__Card__Content">
        <h3 className="AdmReports__Card__Title">{title}</h3>
        {description && <p className="AdmReports__Card__Description">{description}</p>}
        {children}
      </div>

      {/* Pie de Tarjeta: Botón de Acción con Soporte de Spinner y Estados */}
      <div className="AdmReports__Card__Footer">
        <button
          type="button"
          className={buttonClassNames}
          disabled={isButtonDisabled}
          onClick={handleClick}
          title={isButtonDisabled && !isLoading ? 'Acción deshabilitada' : undefined}
          aria-busy={isLoading}
        >
          {isLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <span>{buttonText || 'Descargando...'}</span>
            </>
          ) : (
            <>
              {buttonIcon && <i className={`${buttonIcon} me-1`} aria-hidden="true"></i>}
              <span>{buttonText}</span>
            </>
          )}
        </button>
      </div>
    </article>
  );
}

export { ReportCard };
